<template>
  <div
    @click="isOpen = !isOpen"
    v-clickOutside="handleOutside"
    class="network_selector"
    ref="root"
  >
    <div class="network_selector-content">
      <BaseSvgIcon :name="selectedNetwork.icon" class="network_icon" />
      <span
        v-if="!isMobile"
        class="network_selector-text"
        v-html="$t(selectedNetwork.name)"
      />
      <span
        v-if="isMobile"
        class="network_selector-text"
        v-html="$t(selectedNetwork.subtitle)"
      />
    </div>
    <BaseSvgIcon
      name="arrow-up"
      class="network_arrow"
      :class="{ network_arrow_active: isOpen }"
    />
    <NetworkDropdown
      :isOpen="isOpen"
      :anchorRect="anchorRect"
      @selectNetwork="onSelectNetwork"
    ></NetworkDropdown>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import BaseSvgIcon from "@/shared/ui/BaseSvgIcon/BaseSvgIcon.vue";
import "@/assets/icons/SVG/arrow-up.svg";
import "@/assets/icons/SVG/all-networks.svg";
import NetworkDropdown from "./NetworkDropdown.vue";
import { useStore } from "vuex";

// Props
defineProps({
  placeholder: {
    type: String,
    default: "",
  },
  modelValue: {
    type: [Number, String],
    default: "",
  },
});

const store = useStore();

const networks = [
  {
    name: "network-selector.all-networks",
    subtitle: "",
    icon: "all-networks",
    value: "",
  },
  {
    name: "Bitcoin",
    subtitle: "BTC",
    icon: "bitcoin",
    value: "bitcoin",
  },
  {
    name: "Ethereum",
    subtitle: "ETH",
    icon: "ethereum",
    value: "ethereum",
  },
  {
    name: "Tron",
    subtitle: "TRC-20",
    icon: "tron",
    value: "tron",
  },
  {
    name: "Ton",
    subtitle: "TON",
    icon: "ton",
    value: "ton",
  },
];

const currentNetwork = computed(
  () => store.getters["explorer/getCurrentNetwork"],
);

const selectedNetwork = computed(() => {
  return networks.find((network) => network.value === currentNetwork.value);
});

const handleOutside = (event) => {
  console.log("handleOutside triggered", event);
  isOpen.value = false;
};

const onSelectNetwork = () => {
  isOpen.value = false;
};

// Data
const isOpen = ref(false);
const root = ref(null);
const anchorRect = ref(null);

const updateRect = () => {
  if (root.value) {
    anchorRect.value = root.value.getBoundingClientRect();
  }
};

const onWindowChange = () => {
  if (isOpen.value) {
    updateRect();
  }
};

watch(isOpen, (open) => {
  if (open) {
    updateRect();
    window.addEventListener("scroll", onWindowChange, { passive: true });
    window.addEventListener("resize", onWindowChange, { passive: true });
  } else {
    window.removeEventListener("scroll", onWindowChange);
    window.removeEventListener("resize", onWindowChange);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onWindowChange);
  window.removeEventListener("resize", onWindowChange);
});
</script>

<style lang="scss" scoped>
.network {
  &_selector {
    padding: 6px 10px;
    position: relative;

    height: 34px;
    width: 154px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    z-index: 1;
    transition: 0.3s ease-out;

    @media (width < 768px) {
      width: 100%;
    }

    &-text {
      white-space: nowrap;
      line-height: 150%;
      font-size: 14px;
      font-weight: 500;

      @media (width < 768px) {
        display: none;
      }
    }

    &-content {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    &:hover {
      background: #232323;
    }
  }

  &_arrow {
    transition: 0.3s ease-out;
    transform: rotate(180deg);

    &_active {
      transform: rotate(0deg);
    }
  }

  &_icon {
    width: 24px;
    height: 24px;

    @media (width < 768px) {
      margin-right: 6px;
    }
  }
}
</style>
